import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import { AppHeader } from "../../components/layout/AppHeader";
import PageContainer from "../../components/PageContainer";
import AIScribePanel from "../../components/AIScribePanel";
import { DEV_DOCTOR_ID, DEV_PATIENT_ID, IS_DEV } from "../../constants";
import { DiagnosisAPI, PatientInfo } from "../../services/diagnosis.service";
import { getPatientByDisplayId } from "../../services/patient.service";
import { SoapAPI, SoapApiError, UserInfo } from "../../services/soap.service";
import { db } from "../../db/schema";
import { unwrapFromStorage } from "../../db/encryption-bridge";
import type { PatientRecord, VitalsRecord } from "../../db/db.types";
import { actionErrorMessage, isBackendUnreachable, isPermissionDenied } from "../../utils/network";
import type { AiScribeSoapNote } from "../../services/ai-scribe.service";
import { handleCredentialError } from "../../components/CredentialGate";
import { AccessDenied } from "../../components/RequirePermission";

// ─── Types ────────────────────────────────────────────────────────────────────
interface SoapNote {
  chiefComplaint: string;
  hpi: string;
  ros: string;
  physicalExam: string;
  labResults: string;
  primaryDx: string;
  differentialDx: string;
  clinicalImpression: string;
  medications: string;
  procedures: string;
  followUp: string;
  patientEducation: string;
  referrals: string;
}

type VitalStatus = "normal" | "warning" | "critical";

interface VitalSign {
  label: string;
  value: string;
  unit: string;
  status: VitalStatus;
}

interface SectionTheme {
  avatarBg: string;
  accentField: string;
  accentRing: string;
  vitalCardBg?: string;
  statusDot?: Record<VitalStatus, string>;
}

// ─── Section themes ────────────────────────────────────────────────────────────
const THEMES: Record<"S" | "O" | "A" | "P", SectionTheme> = {
  S: {
    avatarBg: "bg-gradient-to-br from-cyan-400 to-blue-500",
    accentField: "bg-blue-50 border-blue-200",
    accentRing: "focus:ring-blue-300 focus:border-blue-400",
  },
  O: {
    avatarBg: "bg-gradient-to-br from-emerald-400 to-green-600",
    accentField: "bg-green-50 border-green-200",
    accentRing: "focus:ring-green-300 focus:border-green-400",
    vitalCardBg: "bg-green-50 border-green-100",
    statusDot: { normal: "bg-green-400", warning: "bg-yellow-400", critical: "bg-red-400" },
  },
  A: {
    avatarBg: "bg-gradient-to-br from-amber-400 to-orange-500",
    accentField: "bg-amber-50 border-amber-200",
    accentRing: "focus:ring-amber-300 focus:border-amber-400",
  },
  P: {
    avatarBg: "bg-gradient-to-br from-violet-400 to-purple-600",
    accentField: "bg-purple-50 border-purple-200",
    accentRing: "focus:ring-purple-300 focus:border-purple-400",
  },
};

const INITIAL_SOAP: SoapNote = {
  chiefComplaint: "", hpi: "", ros: "",
  physicalExam: "", labResults: "",
  primaryDx: "", differentialDx: "", clinicalImpression: "",
  medications: "", procedures: "", followUp: "", patientEducation: "", referrals: "",
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
function calcAge(dob: string): number {
  const birth = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

function vitalStatus(label: string, value: number): VitalStatus {
  if (label === "Heart Rate") return value < 60 || value > 100 ? "warning" : "normal";
  if (label === "Blood Pressure (Systolic)") return value > 140 ? "critical" : value > 120 ? "warning" : "normal";
  if (label === "Temperature") return value > 103 ? "critical" : value > 99.5 ? "warning" : "normal";
  if (label === "Oxygen Saturation") return value < 95 ? "critical" : value < 98 ? "warning" : "normal";
  return "normal";
}

// ─── Sub-components ──────────────────────────────────────────────────────────
const Field: React.FC<{
  label: string; value: string; onChange: (v: string) => void;
  multiline?: boolean; rows?: number; accent?: boolean; theme?: SectionTheme;
}> = ({ label, value, onChange, multiline = true, rows = 3, accent = false, theme }) => {
  const base = "w-full rounded-lg border px-3 py-2.5 text-base sm:text-sm text-text outline-none focus:ring-1 transition resize-none";
  const style = accent && theme ? `${theme.accentField} ${theme.accentRing}` : "bg-white border-slate-200 focus:border-brand focus:ring-brand";
  return (
    <div className="flex flex-col gap-1 mb-3 last:mb-0">
      <label className="text-sm text-slate-500">{label}</label>
      {multiline
        ? <textarea rows={rows} value={value} onChange={e => onChange(e.target.value)} className={`${base} ${style}`} />
        : <input type="text" value={value} onChange={e => onChange(e.target.value)} className={`${base} ${style}`} />}
    </div>
  );
};

const SectionHeader: React.FC<{ letter: string; theme: SectionTheme; title: string; subtitle: string }> = ({ letter, theme, title, subtitle }) => (
  <div className="flex items-start gap-3 mb-5">
    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 ${theme.avatarBg}`}>{letter}</div>
    <div>
      <h2 className="text-base font-semibold text-text">{title}</h2>
      <p className="text-sm text-slate-500">{subtitle}</p>
    </div>
  </div>
);

const VitalCard: React.FC<{ vital: VitalSign }> = ({ vital }) => {
  const dots: Record<VitalStatus, string> = { normal: "bg-green-400", warning: "bg-yellow-400", critical: "bg-red-400" };
  const vals: Record<VitalStatus, string> = { normal: "text-green-700", warning: "text-yellow-700", critical: "text-red-700" };
  return (
    <div className="bg-green-50 rounded-xl p-3 border border-green-100">
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dots[vital.status]}`} />
        <span className="text-xs text-slate-500 leading-tight">{vital.label}</span>
      </div>
      <span className={`text-lg font-semibold ${vals[vital.status]}`}>{vital.value}</span>
      <span className="text-xs text-slate-400 ml-1">{vital.unit}</span>
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────
const SoapDiagnosisPad: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // visit_id has no dev fallback — SOAP note cannot be saved without a real visit.
  const visitId   = searchParams.get("visit_id")   ?? "";
  const rawPatientId = searchParams.get("patient_id") || (IS_DEV ? DEV_PATIENT_ID : "");
  const doctorId     = searchParams.get("doctor_id")  || (IS_DEV ? DEV_DOCTOR_ID  : "");
  const isPatientDisplayId = rawPatientId.toUpperCase().startsWith("PAT-") && !rawPatientId.includes("?");
  const [patientId, setPatientId] = useState(isPatientDisplayId ? "" : rawPatientId);

  const [soap, setSoap] = useState<SoapNote>(INITIAL_SOAP);
  const [vitals, setVitals] = useState<VitalSign[]>([]);
  const [patient, setPatient] = useState<PatientInfo | null>(null);
  const [doctor, setDoctor] = useState<UserInfo | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [loading, setLoading] = useState(true);
  // Server refused the load (403) — holds its reason; null means "not refused".
  const [accessDenied, setAccessDenied] = useState<string | null>(null);
  const isDirtyRef = useRef(false);
  const visitIdRef = useRef(visitId);
  const soapRef = useRef<SoapNote>(INITIAL_SOAP);
  useEffect(() => { visitIdRef.current = visitId; }, [visitId]);
  useEffect(() => { soapRef.current = soap; }, [soap]);

  // Track current step for resume routing
  useEffect(() => {
    if (!visitId) return;
    DiagnosisAPI.updateVisitStep(visitId, "soap-diagnosis-pad").catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Online / offline
  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  // ── Resolve display_id → UUID ────────────────────────────────────────────────
  useEffect(() => {
    if (!isPatientDisplayId) return;
    getPatientByDisplayId(rawPatientId)
      .then(p => setPatientId(p.patient_id ?? ""))
      .catch(() => { /* patient not found — page will show loading state */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPatientDisplayId, rawPatientId]);

  // Instant pre-fill from AI Scribe result (passed via navigation state)
  useEffect(() => {
    const state = location.state as { aiScribeResult?: any } | null;
    if (!state?.aiScribeResult) return;
    const r = state.aiScribeResult;
    const s = r.soap_note || {};
    const p = r.prescription_document || {};
    const meds = (p.medications || []).map((m: any) =>
      `${m.drug_name} ${m.dosage || ""} ${m.frequency || ""} x ${m.duration_days || ""}d${m.timing ? ` (${m.timing})` : ""}`
    ).join("\n");
    const toStr = (v: any): string => {
      if (!v) return "";
      if (typeof v === "string") return v;
      if (Array.isArray(v)) return v.map(toStr).join(", ");
      if (typeof v === "object") return Object.values(v).map(toStr).join("; ");
      return String(v);
    };
    setSoap({
      chiefComplaint: toStr(s.chief_complaint),
      hpi: toStr(s.history_of_present_illness),
      ros: toStr(s.review_of_systems),
      physicalExam: toStr(s.physical_examination),
      labResults: "",
      primaryDx: toStr(s.primary_diagnosis || s.assessment),
      differentialDx: toStr(s.differential_diagnosis),
      clinicalImpression: toStr(s.clinical_impression || s.assessment),
      medications: meds,
      procedures: Array.isArray(p.investigations) ? p.investigations.join(", ") : (p.investigations || ""),
      followUp: p.follow_up || "",
      patientEducation: p.advice || "",
      referrals: "",
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load all data on mount
  useEffect(() => {
    if (!visitId || !patientId) { setLoading(false); return; }

    const loadData = async () => {
      try {
        const [visitRes, patientRes] = await Promise.all([
          DiagnosisAPI.getVisit(visitId),
          DiagnosisAPI.getPatient(patientId),
        ]);

        setPatient(patientRes);

        // Doctor fetch is non-critical — don't let a 404 blank the whole page
        if (doctorId) {
          SoapAPI.getUser(doctorId).then(setDoctor).catch(() => undefined);
        }

        // Map vitals from DB
        if (visitRes.vitals) {
          const v = visitRes.vitals;
          const built: VitalSign[] = [];
          if (v.blood_pressure_systolic && v.blood_pressure_diastolic)
            built.push({ label: "Blood Pressure", value: `${v.blood_pressure_systolic}/${v.blood_pressure_diastolic}`, unit: "mmHg", status: v.blood_pressure_systolic > 140 ? "critical" : v.blood_pressure_systolic > 120 ? "warning" : "normal" });
          if (v.heart_rate)
            built.push({ label: "Heart Rate", value: `${v.heart_rate}`, unit: "bpm", status: vitalStatus("Heart Rate", v.heart_rate) });
          if (v.temperature)
            built.push({ label: "Temperature", value: `${v.temperature}`, unit: "°F", status: vitalStatus("Temperature", Number(v.temperature)) });
          if (v.respiratory_rate)
            built.push({ label: "Respiratory Rate", value: `${v.respiratory_rate}`, unit: "/min", status: "normal" });
          if (v.oxygen_saturation)
            built.push({ label: "SpO2", value: `${v.oxygen_saturation}`, unit: "%", status: vitalStatus("Oxygen Saturation", v.oxygen_saturation) });
          if (v.bmi)
            built.push({ label: "BMI", value: `${v.bmi}`, unit: "kg/m²", status: "normal" });
          if (v.blood_sugar)
            built.push({ label: "Blood Sugar", value: `${v.blood_sugar}`, unit: "mg/dL", status: Number(v.blood_sugar) > 140 ? "warning" : "normal" });
          setVitals(built);
        }

        // Try to load existing SOAP note (skip if AI Scribe already pre-filled)
        const hasAiScribe = !!(location.state as any)?.aiScribeResult;
        if (!hasAiScribe) {
          try {
            const noteRes = await SoapAPI.getNote(visitId);
            setSoap({
              chiefComplaint: noteRes.chief_complaint || "",
              hpi: noteRes.hpi || "",
              ros: noteRes.ros || "",
              physicalExam: noteRes.physical_exam || "",
              labResults: noteRes.lab_results || "",
              primaryDx: noteRes.primary_dx || "",
              differentialDx: noteRes.differential_dx || "",
              clinicalImpression: noteRes.clinical_impression || "",
              medications: noteRes.medications_text || "",
              procedures: noteRes.procedures || "",
              followUp: noteRes.follow_up || "",
              patientEducation: noteRes.patient_education || "",
              referrals: noteRes.referrals || "",
            });
          } catch (e) {
            if (!(e instanceof SoapApiError && e.statusCode === 404)) throw e;
            // No SOAP note yet — start with blank form
          }
        }
      } catch (err) {
        // A 403 is a refusal, not an outage: the Dexie fallback below would
        // render the cached patient and vitals the grant was revoked for.
        if (isPermissionDenied(err)) {
          setAccessDenied(actionErrorMessage(err, "You don't have access to this visit."));
        } else if (isBackendUnreachable(err)) {
          // Offline fallback — populate patient and visit data from Dexie.
          try {
            const [rawPatient, rawVitalsRows] = await Promise.all([
              db.patients.get(patientId),
              db.vitals.where("visit_id").equals(visitId).toArray(),
            ]);
            if (rawPatient) {
              const p = await unwrapFromStorage<PatientRecord>(rawPatient as unknown as Record<string, unknown>);
              setPatient({
                patient_id:    p.patient_id,
                first_name:    (p as any).first_name  ?? "",
                last_name:     (p as any).last_name   ?? "",
                date_of_birth: (p as any).date_of_birth ?? "",
                gender:        (p as any).gender      ?? "",
                contact_number: p.contact_number,
              } as PatientInfo);
            }
            if (rawVitalsRows.length > 0) {
              const vt = await unwrapFromStorage<VitalsRecord>(rawVitalsRows[0] as unknown as Record<string, unknown>);
              const built: VitalSign[] = [];
              if (vt.blood_pressure_systolic && vt.blood_pressure_diastolic)
                built.push({ label: "Blood Pressure", value: `${vt.blood_pressure_systolic}/${vt.blood_pressure_diastolic}`, unit: "mmHg", status: vt.blood_pressure_systolic > 140 ? "critical" : vt.blood_pressure_systolic > 120 ? "warning" : "normal" });
              if (vt.heart_rate)
                built.push({ label: "Heart Rate", value: `${vt.heart_rate}`, unit: "bpm", status: vitalStatus("Heart Rate", vt.heart_rate) });
              if (vt.temperature)
                built.push({ label: "Temperature", value: `${vt.temperature}`, unit: "°F", status: vitalStatus("Temperature", Number(vt.temperature)) });
              if (vt.oxygen_saturation)
                built.push({ label: "SpO2", value: `${vt.oxygen_saturation}`, unit: "%", status: vitalStatus("Oxygen Saturation", vt.oxygen_saturation) });
              setVitals(built);
            }
          } catch { /* non-fatal — show empty form */ }
        }
        // For any error (network or otherwise), SOAP note starts blank — that's correct.
      } finally {
        setLoading(false);
        isDirtyRef.current = false;
      }
    };
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitId, patientId, doctorId]);

  // Mark dirty on any change after initial load
  useEffect(() => {
    if (!loading) isDirtyRef.current = true;
  }, [soap, loading]);

  const saveDraft = useCallback(async () => {
    const vid = visitIdRef.current;
    const s = soapRef.current;
    if (!vid) return;
    setSaveStatus("saving");
    try {
      await SoapAPI.upsertNote(vid, {
        chief_complaint: s.chiefComplaint || undefined,
        hpi: s.hpi || undefined,
        ros: s.ros || undefined,
        physical_exam: s.physicalExam || undefined,
        lab_results: s.labResults || undefined,
        primary_dx: s.primaryDx || undefined,
        differential_dx: s.differentialDx || undefined,
        clinical_impression: s.clinicalImpression || undefined,
        medications_text: s.medications || undefined,
        procedures: s.procedures || undefined,
        follow_up: s.followUp || undefined,
        patient_education: s.patientEducation || undefined,
        referrals: s.referrals || undefined,
      });
      // Sync key fields to the visit record so ContinueDiagnosis picks them up
      DiagnosisAPI.patchVisit(vid, {
        chief_complaint: s.chiefComplaint || undefined,
        notes: s.clinicalImpression || undefined,
        advice: s.patientEducation || undefined,
        lab_tests: s.procedures ? s.procedures.split(/[,;\n]/).map(t => t.trim()).filter(Boolean) : undefined,
        assessment: s.primaryDx || undefined,
      } as any).catch(() => {}); // best-effort — SOAP note is the source of truth
      setSaveStatus("saved");
      isDirtyRef.current = false;
      setTimeout(() => setSaveStatus(prev => prev === "saved" ? "idle" : prev), 2000);
    } catch (err) {
      if (handleCredentialError(err, navigate)) return;
      setSaveStatus("error");
      setTimeout(() => setSaveStatus(prev => prev === "error" ? "idle" : prev), 3000);
    }
  }, [navigate]); // otherwise stable — reads via refs; navigate has a stable identity

  // Debounce auto-save: 1.5s after last change
  useEffect(() => {
    if (!isDirtyRef.current) return;
    const timer = setTimeout(() => { if (isDirtyRef.current) saveDraft(); }, 1500);
    return () => clearTimeout(timer);
  }, [soap, saveDraft]);

  const set = (key: keyof SoapNote) => (val: string) => setSoap(prev => ({ ...prev, [key]: val }));

  // AI Scribe callback — fill SOAP fields from Gemini response
  const handleAiSoapFilled = useCallback((aiSoap: AiScribeSoapNote) => {
    setSoap(prev => ({
      ...prev,
      chiefComplaint: aiSoap.chief_complaint || prev.chiefComplaint,
      hpi: aiSoap.history_of_present_illness || prev.hpi,
      ros: aiSoap.review_of_systems || prev.ros,
      physicalExam: aiSoap.physical_examination || prev.physicalExam,
      primaryDx: aiSoap.assessment || prev.primaryDx,
      clinicalImpression: aiSoap.assessment || prev.clinicalImpression,
      medications: aiSoap.plan || prev.medications,
    }));
  }, []);

  // ── PDF Export ───────────────────────────────────────────────────────────────
  const [pdfExporting, setPdfExporting] = useState(false);
  const [pdfExportError, setPdfExportError] = useState<string | null>(null);

  const handleExportPdf = async () => {
    if (!visitId) return;
    setPdfExporting(true);
    setPdfExportError(null);
    try {
      if (isDirtyRef.current) await saveDraft();

      const prescriptions = await DiagnosisAPI.getPrescriptions(visitId);
      if (!prescriptions?.length) {
        setPdfExportError("No prescription found — save a prescription before exporting.");
        return;
      }
      const rxId = prescriptions[0].prescription_id;
      const result = await DiagnosisAPI.generatePdf(visitId, rxId);
      window.open(result.pdf_url, "_blank", "noopener,noreferrer");
    } catch {
      setPdfExportError("Failed to generate PDF — please try again.");
    } finally {
      setPdfExporting(false);
    }
  };

  const patientName = patient ? `${patient.first_name} ${patient.last_name}` : "—";
  const patientAge = patient?.date_of_birth ? calcAge(patient.date_of_birth) : "—";
  const doctorName = doctor ? `Dr. ${doctor.first_name} ${doctor.last_name}` : "—";

  if (accessDenied) {
    return (
      <div className="min-h-screen bg-[#f5f5f7]">
        <Sidebar />
        <div className="lg:pl-[var(--sidebar-w)] transition-[padding-left] duration-300 ease-in-out min-h-screen">
          <AppHeader title="SOAP Note" />
          <AccessDenied label="this visit" detail={accessDenied} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f5f7]">
      <Sidebar />
      <div className="lg:pl-[var(--sidebar-w)] transition-[padding-left] duration-300 ease-in-out min-h-screen flex flex-col p-2 sm:p-3 pt-14 lg:pt-2">
      <PageContainer className="flex flex-col overflow-hidden min-h-0 flex-1">
        <AppHeader title="SOAP Note" />

        {/* ── Header ── */}
        <header className="bg-white border-b border-border flex flex-wrap items-center gap-3 px-5 py-3 flex-shrink-0">
          <button onClick={async () => { if (isDirtyRef.current) await saveDraft(); navigate(`/90sdiagnosis?patient_id=${rawPatientId}&doctor_id=${doctorId}&visit_id=${visitId}`); }} className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 transition text-slate-500 flex-shrink-0">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-text truncate">SOAP Note – Draft</span>
              <span className="flex items-center gap-1 text-xs text-white font-medium px-2.5 py-0.5 rounded-full flex-shrink-0"
                style={{ background: "linear-gradient(135deg, #f472b6, #a855f7)" }}>✦ AI Generated</span>
            </div>
            <p className="text-xs text-slate-400 mt-0.5">Auto-generated from consultation recording</p>
          </div>

          {!isOnline && (
            <span className="text-xs bg-yellow-500 text-black px-2 py-0.5 rounded-full font-medium flex-shrink-0">Offline</span>
          )}

          <button className="flex items-center gap-1.5 text-xs border border-slate-200 text-slate-600 px-3 min-h-[44px] rounded-lg hover:bg-slate-50 transition flex-shrink-0">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M9 13l6.5-6.5a2 2 0 012.828 2.828L11.828 15.828a4 4 0 01-1.414.94l-3 1 1-3a4 4 0 01.94-1.414z" />
            </svg>
            Edit Note
          </button>

          <button
            onClick={saveDraft}
            disabled={saveStatus === "saving" || !visitId}
            className="flex items-center gap-1.5 text-xs text-white px-3 min-h-[44px] rounded-lg disabled:opacity-50 transition flex-shrink-0"
            style={{ background: "linear-gradient(135deg, #22d3ee, #3b82f6)" }}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
            </svg>
            {saveStatus === "saving" ? "Saving…" : saveStatus === "saved" ? "Saved ✓" : saveStatus === "error" ? "Error" : "Save Draft"}
          </button>

        </header>

        {/* ── Patient info ── */}
        <div className="bg-white border-b border-border px-6 py-3 flex-shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white flex-shrink-0 text-sm font-bold">
              {patientName.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-text truncate">{loading ? "Loading…" : patientName}</div>
              <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap mt-0.5">
                <span>{patientAge} yrs</span>
                <span className="text-slate-300">|</span>
                <span>{patient?.gender ?? "—"}</span>
                <span className="text-slate-300">|</span>
                <span>{loading ? "—" : doctorName}</span>
              </div>
            </div>
            <div className="flex items-center gap-3 text-xs text-slate-500 flex-shrink-0">
              <span>{new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
              <span>{new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
            </div>
          </div>
        </div>

        {/* ── SOAP Content ── */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-6 py-5 space-y-5 pb-8">

            {/* AI Scribe — record / upload audio → auto-fill SOAP + Prescription */}
            {visitId && (
              <AIScribePanel
                visitId={visitId}
                onSoapFilled={handleAiSoapFilled}
              />
            )}

            {/* S – Subjective */}
            <div className="bg-white rounded-2xl shadow-card p-6">
              <SectionHeader letter="S" theme={THEMES.S} title="Subjective" subtitle="Patient's reported symptoms and history" />
              <Field label="Chief Complaint" value={soap.chiefComplaint} onChange={set("chiefComplaint")} multiline={false} accent theme={THEMES.S} />
              <Field label="History of Present Illness (HPI)" value={soap.hpi} onChange={set("hpi")} rows={4} />
              <Field label="Review of Systems (ROS)" value={soap.ros} onChange={set("ros")} rows={3} />
            </div>

            {/* O – Objective */}
            <div className="bg-white rounded-2xl shadow-card p-6">
              <SectionHeader letter="O" theme={THEMES.O} title="Objective" subtitle="Clinical findings and measurements" />
              {vitals.length > 0 && (
                <>
                  <p className="text-sm text-slate-500 mb-2">Vital Signs</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
                    {vitals.map(v => <VitalCard key={v.label} vital={v} />)}
                  </div>
                </>
              )}
              <Field label="Physical Examination" value={soap.physicalExam} onChange={set("physicalExam")} rows={4} accent theme={THEMES.O} />
              <Field label="Lab Results & Diagnostics" value={soap.labResults} onChange={set("labResults")} rows={3} />
            </div>

            {/* A – Assessment */}
            <div className="bg-white rounded-2xl shadow-card p-6">
              <SectionHeader letter="A" theme={THEMES.A} title="Assessment" subtitle="Clinical diagnosis and interpretation" />
              <Field label="Primary Diagnosis" value={soap.primaryDx} onChange={set("primaryDx")} multiline={false} accent theme={THEMES.A} />
              <Field label="Differential Diagnosis" value={soap.differentialDx} onChange={set("differentialDx")} rows={3} />
              <Field label="Clinical Impression" value={soap.clinicalImpression} onChange={set("clinicalImpression")} rows={3} />
            </div>

            {/* P – Plan */}
            <div className="bg-white rounded-2xl shadow-card p-6">
              <SectionHeader letter="P" theme={THEMES.P} title="Plan" subtitle="Treatment and management plan" />
              <Field label="Medications Prescribed" value={soap.medications} onChange={set("medications")} rows={4} accent theme={THEMES.P} />
              <Field label="Procedures & Tests Ordered" value={soap.procedures} onChange={set("procedures")} rows={3} />
              <Field label="Follow-up Instructions" value={soap.followUp} onChange={set("followUp")} rows={2} />
              <Field label="Patient Education" value={soap.patientEducation} onChange={set("patientEducation")} rows={2} />
              <Field label="Referrals" value={soap.referrals} onChange={set("referrals")} rows={2} />
            </div>

          </div>
        </div>

        {/* ── Footer ── */}
        <footer className="bg-white border-t border-slate-100 px-4 sm:px-6 py-4 flex-shrink-0">
          <div className="bg-white px-5 py-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <svg className="w-5 h-5 text-slate-400 flex-shrink-0 hidden sm:block" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09z" />
                <path d="M18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456z" />
              </svg>
              <span className="text-xs text-slate-500 truncate">This note was auto-generated by AI Scribe. Please review and edit as needed.</span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {pdfExportError && (
                <span className="text-xs text-red-500 max-w-[200px] truncate">{pdfExportError}</span>
              )}
              <button
                onClick={handleExportPdf}
                disabled={pdfExporting}
                className="flex items-center gap-1.5 text-xs border border-slate-200 text-slate-600 px-3.5 py-2 rounded-lg hover:bg-slate-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {pdfExporting ? (
                  <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                ) : (
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="9" y1="13" x2="15" y2="13" /><line x1="9" y1="17" x2="13" y2="17" />
                  </svg>
                )}
                {pdfExporting ? "Generating…" : "Export as PDF"}
              </button>
              <button
                onClick={() => {
                  const scribeResult = (location.state as { aiScribeResult?: any } | null)?.aiScribeResult;
                  navigate(
                    `/continue-diagnosis?visit_id=${visitId}&patient_id=${rawPatientId}&doctor_id=${doctorId}`,
                    scribeResult ? { state: { prescription_document: scribeResult.prescription_document, soap_note: scribeResult.soap_note } } : undefined
                  );
                }}
                className="flex items-center gap-1.5 text-xs text-white font-medium px-4 py-2 rounded-lg transition"
                style={{ background: "linear-gradient(135deg, #34d399, #059669)" }}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                Finalize &amp; Sign
              </button>
            </div>
          </div>
        </footer>

      </PageContainer>
      </div>
    </div>
  );
};

export default SoapDiagnosisPad;
